import { SaleProduct_Type as SaleProductType } from "gen/txn/models_pb"
import { Decimal } from "shared-ts/classes/decimal"
import { FractionalMoney, Money } from "shared-ts/classes/money"
import { UUID } from "shared-ts/classes/uuid"
import {
  isValidUomSnapshot,
  safeConvertQuantityToUoMQuantity,
} from "shared-ts/libs/product/unitsOfMeasure.helpers"

import { SaleLink, SaleProduct } from "../types/SaleForm.types"

export function hasZeroQuantitySaleProduct(
  saleProducts: SaleProduct[]
): boolean {
  return saleProducts.some((sp) => signedQuantity(sp).isZero())
}

export function hasOnlyFullyClosedOutProducts(
  saleProducts: SaleProduct[],
  childSaleLinksBySaleProductId: Record<string, SaleLink[]>
): boolean {
  const activeLines = saleProducts.filter((sp) => sp.isActive)
  if (activeLines.length === 0) {
    return false
  }

  return activeLines.every(
    (sp) =>
      (childSaleLinksBySaleProductId[UUID.idString(sp.id)]?.length ?? 0) > 0 &&
      signedQuantity(sp).isZero()
  )
}

export function signedQuantity(sp: SaleProduct): Decimal {
  if (isReturn(sp)) {
    return Decimal.fromPBOrZero(sp.quantityReturnedV2).mult(
      Decimal.fromBigSource(-1)
    )
  }
  return Decimal.fromPBOrZero(sp.quantityV2)
}

export function openQuantity(sp: SaleProduct): Decimal {
  if (isReturn(sp)) {
    return Decimal.fromPBOrZero(sp.quantityReturnedV2)
  }
  return Decimal.fromPBOrZero(sp.quantityV2)
}

export function isLinkedReturn(sp: SaleProduct) {
  return sp.type === SaleProductType.RETURN
}

export function isUnlinkedReturn(sp: SaleProduct) {
  return sp.type === SaleProductType.UNLINKED_RETURN
}
export function isReturnType(spt: SaleProductType) {
  return (
    spt === SaleProductType.RETURN || spt === SaleProductType.UNLINKED_RETURN
  )
}

export function isReturn(sp: SaleProduct) {
  return isReturnType(sp.type)
}

export function isSpecialOrderSaleProduct(sp: SaleProduct) {
  return sp.type === SaleProductType.SALE && sp.isSpecialOrderableV2
}

export function returnedFraction(sp: SaleProduct): string | undefined {
  let sold = sp.quantityV2
  let returned = sp.quantityReturnedV2
  const conversionFactorToBase = sp.uomSnapshot?.conversionFactorToBase
  if (sp.uomId && sp.quantityUom) {
    if (sp.quantityReturnedUom) {
      sold = sp.quantityUom
      returned = sp.quantityReturnedUom
    } else if (
      returned &&
      conversionFactorToBase &&
      isValidUomSnapshot(sp.uomSnapshot)
    ) {
      sold = sp.quantityUom
      returned = safeConvertQuantityToUoMQuantity(
        Decimal.fromPB(returned),
        Decimal.fromPB(conversionFactorToBase)
      )
    }
  }
  if (!sold || !returned) {
    return undefined
  }
  const returnedQty = Decimal.fromPBOrZero(returned).abs()
  const soldQty = Decimal.fromPBOrZero(sold).abs()
  if (returnedQty.isZero() || soldQty.isZero()) {
    return undefined
  }
  return `${returnedQty.toString()} of ${soldQty.toString()} returned`
}

export function returnSaleProductIds(saleProducts: SaleProduct[]): Set<string> {
  return new Set(
    saleProducts.filter(isReturn).map((sp) => UUID.idString(sp.id))
  )
}

export function getQuantityMin(
  saleProduct: SaleProduct,
  quantityRemainingBySaleProductId: Record<string, Decimal>,
  areReturnsPermitted: boolean,
  isPartiallyClosedOut: boolean
): Decimal | undefined {
  switch (saleProduct.type) {
    case SaleProductType.UNLINKED_RETURN:
      return undefined
    case SaleProductType.RETURN: {
      const quantityRemaining =
        quantityRemainingBySaleProductId[
          UUID.idString(saleProduct.returnedFromSaleProductId)
        ]
      return quantityRemaining === undefined
        ? Decimal.zero()
        : quantityRemaining.mult(Decimal.fromBigSource(-1))
    }
    case SaleProductType.SALE:
      if (isPartiallyClosedOut) {
        return Decimal.zero()
      }
      return areReturnsPermitted ? undefined : Decimal.fromBigSource(1)
    default:
      return undefined
  }
}

export function getQuantityMax(
  saleProduct: SaleProduct,
  isPartiallyClosedOut: boolean
): Decimal | undefined {
  switch (saleProduct.type) {
    case SaleProductType.UNLINKED_RETURN:
      return isPartiallyClosedOut ? Decimal.zero() : undefined
    case SaleProductType.RETURN:
      return isPartiallyClosedOut ? Decimal.zero() : Decimal.fromBigSource(-1)
    case SaleProductType.SALE:
      return undefined
    default:
      return undefined
  }
}

export function getGiftCardOverwrittenBalance(
  saleProduct: SaleProduct
): Money | undefined {
  if (!saleProduct.giftCardId || !saleProduct.giftCardInitialBalance) {
    return undefined
  }

  const initialBalance = Money.fromPB(saleProduct.giftCardInitialBalance)

  if (
    Money.eqFromPB(saleProduct.price, initialBalance) ||
    initialBalance.isZero()
  ) {
    return undefined
  }

  return initialBalance
}

export function effectiveSaleProductPriceFractional(
  sp: SaleProduct
): FractionalMoney {
  return sp.priceFractional
    ? FractionalMoney.fromPB(sp.priceFractional)
    : FractionalMoney.fromMoney(Money.fromPB(sp.price))
}

export function saleProductLineTotal(sp: SaleProduct): Money {
  return effectiveSaleProductPriceFractional(sp)
    .mult(signedQuantity(sp))
    .toMoney()
}
